import React, {useEffect, useState} from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocumentMetadataStatusButton} from 'app/general/structures/DocumentMetadataStatusButton';
import {
  fetchSubscriptionStatus,
  subscribe,
  unsubscribe,
} from 'app/notifly/actions';
import {useModelChangeSubscription} from 'app/subscriptions';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

type Props<T> = {
  document: T;
  contentType: 'procedure' | 'proceduregroup' | 'policy' | 'tasktemplate';
  customTooltip?: (subscribed: boolean) => React.ReactNode;
};

export const DocumentSubscribedStatusButton = <
  T extends {
    id: number;
  }
>({
  document: {id},
  contentType,
  customTooltip,
}: Props<T>): JSX.Element => {
  const [subscriptionURL, setSubscriptionURL] = useState<void | string>(
    undefined
  );
  const ctName = getContentType(contentType).name;
  const mounted = React.useRef<boolean>(true);
  const [loading, _setLoading] = useState<boolean>(true);
  const setLoading = (loading: boolean) => {
    // avoids changing state after unmount
    if (mounted.current) {
      _setLoading(loading);
    }
  };
  const actions = useAsyncActions({
    subscribe,
    unsubscribe,
  });

  const fetchStatus = () =>
    fetchSubscriptionStatus(id, contentType)
      .then(({results}) => {
        if (mounted.current) {
          setSubscriptionURL(results.length ? results[0].url : '');
        }
      })
      .then(
        () => setLoading(false),
        () => setLoading(false)
      );

  useEffect(() => {
    fetchStatus();
    return () => {
      mounted.current = false;
    };
  }, []);

  // model-change ws messages use 'process' as ct, not 'processgroup'
  const normalizedCt =
    contentType === 'proceduregroup' ? 'process' : contentType;
  useModelChangeSubscription(
    (message, {runWhenAny, modelIdMatch, anyRelatedMatch}) =>
      runWhenAny(
        message,
        [
          modelIdMatch(normalizedCt, id),
          anyRelatedMatch({
            content_type: normalizedCt,
            id,
          }),
        ],
        fetchStatus
      ),
    [id, normalizedCt]
  );

  const updateSubscription = () => {
    setLoading(true);
    let prom: Promise<unknown>;

    if (subscriptionURL !== '') {
      // remove subscription
      prom = actions
        // we know at this point subscriptionURL can't be null, but Typescript doesn't
        .unsubscribe(subscriptionURL || '', contentType)
        .then(() => setSubscriptionURL(''));
    } else {
      prom = actions
        .subscribe(id, contentType)
        .then(({url}) => setSubscriptionURL(url));
    }

    prom.then(
      () => setLoading(false),
      () => setLoading(false)
    );
  };

  const customTooltipContent =
    customTooltip != null ? customTooltip(subscriptionURL !== '') : null;

  return (
    <LazyTooltip
      tooltip={
        customTooltipContent ||
        (subscriptionURL
          ? gt(
              `You’re receiving notifications because you’re subscribed to this ${ctName}.`
            )
          : gt(`You’re not receiving notifications from this ${ctName}.`))
      }
    >
      <DocumentMetadataStatusButton
        disabled={loading}
        active={!!subscriptionURL}
        onClick={updateSubscription}
      >
        <i
          className={cs(
            'fa',
            'fa-fw',
            loading
              ? 'fa-circle-notch fa-spin'
              : subscriptionURL
              ? 'fa-bell'
              : 'fa-bell-slash'
          )}
        />
      </DocumentMetadataStatusButton>
    </LazyTooltip>
  );
};
